/* React */
import { useEffect, useId, useState } from 'react'

/* Redux */
import { useSelector, useDispatch } from 'src/redux/hooks'

/* Material */
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogContent from '@mui/material/DialogContent'
import Typography from '@mui/material/Typography'
import Box from '@mui/material/Box'
import Switch from '@mui/material/Switch'

import { Theme } from '@mui/material/styles'
import createStyles from '@mui/styles/createStyles'
import makeStyles from '@mui/styles/makeStyles'

/* Next */
import Link from 'next/link'

/* Shop-Config */
import shopInfos from 'template/infos'
import { analyticsConfig, analyticsConsentDescription, hasOptionalAnalytics } from 'src/functions/analyticsConfig'

const useStyles = makeStyles((_theme: Theme) =>
  createStyles({
    paper: {
      borderRadius: 28,
      boxSizing: 'border-box',
      margin: 0,
      padding: 0,
      overflow: 'hidden',
      maxWidth: 760,
      width: 'calc(100vw - 32px)',
      maxHeight: 'calc(100dvh - 32px)',
      display: 'flex',
      '@supports not (height: 100dvh)': {
        maxHeight: 'calc(100vh - 32px)',
      },
      '@media (max-width: 699px)': {
        width: 'calc(100vw - 20px)',
        maxWidth: 'calc(100vw - 20px)',
        maxHeight: 'calc(100dvh - 20px)',
        borderRadius: 22,
      },
    },
    content: {
      boxSizing: 'border-box',
      width: '100%',
      padding: '44px 32px 32px',
      textAlign: 'center',
      overflowY: 'auto',
      overscrollBehavior: 'contain',
      WebkitOverflowScrolling: 'touch',
      '@media (min-width: 700px)': {
        padding: '52px 56px 44px',
      },
      '@media (max-width: 699px)': {
        padding: '24px 16px 20px',
      },
    },
    cookieBadge: {
      width: 92,
      height: 92,
      borderRadius: '50%',
      display: 'grid',
      placeItems: 'center',
      margin: '0 auto 28px',
      background: '#f6e8cf',
      color: '#8f6438',
      fontSize: 34,
      '@media (max-width: 699px)': {
        width: 68,
        height: 68,
        marginBottom: 20,
        fontSize: 28,
      },
    },
    title: {
      fontWeight: 800,
      color: '#111827',
      marginBottom: 18,
      lineHeight: 1.1,
      maxWidth: '100%',
      overflowWrap: 'break-word',
      '@media (max-width: 699px)': {
        fontSize: '1.35rem',
        lineHeight: 1.2,
      },
    },
    text: {
      color: '#374151',
      fontSize: 17,
      lineHeight: 1.6,
      margin: '0 auto 18px',
      maxWidth: 620,
      overflowWrap: 'break-word',
      '@media (max-width: 699px)': {
        fontSize: 16,
        maxWidth: '100%',
      },
    },
    optionPanel: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 18,
      margin: '26px auto 22px',
      padding: '18px 20px',
      maxWidth: 620,
      border: '1px solid #d1d5db',
      borderRadius: 14,
      background: '#fff',
      textAlign: 'left',
      '@media (max-width: 699px)': {
        alignItems: 'flex-start',
        gap: 12,
        padding: '16px',
      },
    },
    optionTitle: {
      color: '#111827',
      fontSize: 16,
      fontWeight: 800,
      marginBottom: 6,
    },
    optionDescription: {
      color: '#4b5563',
      fontSize: 14,
      lineHeight: 1.45,
      margin: 0,
    },
    switchRoot: {
      flexShrink: 0,
      '& .MuiSwitch-switchBase.Mui-checked': {
        color: '#f0822b',
      },
      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
        backgroundColor: '#f0822b',
      },
    },
    links: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      gap: 18,
      marginBottom: 28,
      fontSize: 16,
      '& a': {
        color: '#374151',
        textDecorationThickness: 1.5,
        textUnderlineOffset: 3,
      },
      '@media (max-width: 699px)': {
        marginBottom: 22,
      },
    },
    actions: {
      display: 'flex',
      gap: 14,
      justifyContent: 'center',
      flexWrap: 'wrap',
    },
    actionButton: {
      minWidth: 190,
      minHeight: 56,
      borderRadius: 16,
      fontSize: 16,
      fontWeight: 700,
      textTransform: 'none',
      boxShadow: 'none',
      '@media (max-width: 699px)': {
        width: '100%',
        minWidth: 0,
      },
    },
    neutralButton: {
      background: '#fff',
      border: '1px solid #d1d5db',
      color: '#1f2937',
      '&:hover': {
        background: '#f9fafb',
      },
    },
    primaryButton: {
      background: '#f0822b',
      color: '#fff',
      '&:hover': {
        background: '#db7424',
        boxShadow: 'none',
      },
    },
    legalNote: {
      marginTop: 18,
      color: '#6b7280',
      fontSize: 12,
      lineHeight: 1.45,
      maxWidth: 620,
      marginLeft: 'auto',
      marginRight: 'auto',
    },
  })
)

const CookieBanner = () => {
  const titleId = useId()
  const classes = useStyles()
  const open = useSelector(store => store.App.showCookieBanner)
  const cookieConsent = useSelector(store => store.App.cookieConsent)
  const dispatch = useDispatch()
  const [analyticsEnabled, setAnalyticsEnabled] = useState(false)
  const isGoogleAnalytics = analyticsConfig.tracker === 'google_analytics'
  const paperStyle = {
    margin: 0,
    width: 'min(760px, calc(100vw - 32px))',
    maxWidth: 'calc(100vw - 32px)',
    maxHeight: 'calc(100dvh - 32px)',
  }

  useEffect(() => {
    if (!open || typeof window === 'undefined') return

    const { body, documentElement } = document
    const scrollY = window.scrollY
    const previousBodyStyles = {
      overflow: body.style.overflow,
      position: body.style.position,
      top: body.style.top,
      width: body.style.width,
    }
    const previousHtmlOverscroll = documentElement.style.overscrollBehavior

    body.style.overflow = 'hidden'
    body.style.position = 'fixed'
    body.style.top = `-${scrollY}px`
    body.style.width = '100%'
    documentElement.style.overscrollBehavior = 'none'

    return () => {
      body.style.overflow = previousBodyStyles.overflow
      body.style.position = previousBodyStyles.position
      body.style.top = previousBodyStyles.top
      body.style.width = previousBodyStyles.width
      documentElement.style.overscrollBehavior = previousHtmlOverscroll
      window.scrollTo(0, scrollY)
    }
  }, [open])

  useEffect(() => {
    if (!open) return
    setAnalyticsEnabled(cookieConsent === 'all')
  }, [cookieConsent, open])

  const close = (_event: unknown, reason?: string) => {
    if (reason === 'backdropClick' || reason === 'escapeKeyDown') return
    dispatch({ type: 'hideCookieBanner' })
  }

  const saveSelection = () => {
    dispatch({
      type: 'setCookieConsent',
      payload: hasOptionalAnalytics && analyticsEnabled ? 'all' : 'necessary',
    })
  }

  const rejectOptionalCookies = () => {
    setAnalyticsEnabled(false)
    dispatch({ type: 'setCookieConsent', payload: 'necessary' })
  }

  const acceptOptionalCookies = () => {
    setAnalyticsEnabled(true)
    dispatch({ type: 'setCookieConsent', payload: 'all' })
  }

  return <Dialog
    open={open}
    aria-labelledby={titleId}
    onClose={close}
    maxWidth={false}
    disableEscapeKeyDown
    scroll="paper"
    PaperProps={{ className: classes.paper, style: paperStyle }}
  >
    <DialogContent className={classes.content}>
      <Box className={classes.cookieBadge} aria-hidden="true">🍪</Box>

      <Typography id={titleId} component="h2" variant="h3" className={classes.title}>
        Cookie-Einstellungen
      </Typography>

      <Typography component="p" className={classes.text}>
        Wir setzen technisch notwendige Cookies ein, damit {shopInfos.name} zuverlässig funktioniert.
      </Typography>

      {hasOptionalAnalytics
        ? <>
          <Box className={classes.optionPanel}>
            <Box>
              <Typography component="p" className={classes.optionTitle}>
                Webanalyse
              </Typography>
              <Typography component="p" className={classes.optionDescription}>
                {analyticsConsentDescription}. Dabei können Cookies oder vergleichbare Technologien
                auf Ihrem Gerät gespeichert und Nutzungsdaten verarbeitet werden.
              </Typography>
            </Box>
            <Switch
              checked={analyticsEnabled}
              onChange={event => setAnalyticsEnabled(event.target.checked)}
              color="default"
              className={classes.switchRoot}
              inputProps={{ 'aria-label': 'Webanalyse erlauben' }}
            />
          </Box>
        </>
        : <Typography component="p" className={classes.text}>
          Wir setzen keine optionalen Analyse- oder Performance-Cookies ein. Ihre Auswahl
          können Sie jederzeit in der Datenschutzerklärung ändern.
        </Typography>
      }

      <Box className={classes.links}>
        <Link href="/datenschutzerklaerung">Datenschutz</Link>
        <span>&bull;</span>
        <Link href="/impressum">Impressum</Link>
      </Box>

      <Box className={classes.actions}>
        <Button
          variant="contained"
          disableElevation
          className={`${classes.actionButton} ${hasOptionalAnalytics ? classes.primaryButton : classes.neutralButton}`}
          onClick={hasOptionalAnalytics ? acceptOptionalCookies : rejectOptionalCookies}
        >
          {hasOptionalAnalytics ? 'Alle akzeptieren' : 'Verstanden'}
        </Button>

        {hasOptionalAnalytics && (
          <>
            <Button
              variant="contained"
              disableElevation
              className={`${classes.actionButton} ${classes.neutralButton}`}
              onClick={saveSelection}
            >
              Auswahl speichern
            </Button>

            <Button
              variant="contained"
              disableElevation
              className={`${classes.actionButton} ${classes.neutralButton}`}
              onClick={rejectOptionalCookies}
            >
              Alle ablehnen
            </Button>
          </>
        )}
      </Box>

      <Typography component="p" className={classes.legalNote}>
        Technisch notwendige Cookies bleiben aktiv, damit Warenkorb, Login und Checkout funktionieren.
        {' '}
        {hasOptionalAnalytics
          ? isGoogleAnalytics
            ? 'Den ersten Seitenaufruf senden wir im Consent-Modus mit deaktivierter Analyse-Speicherung. Weitere Seitenaufrufe, Webanalyse-Cookies und detaillierte Webanalyse aktivieren wir erst nach Ihrer Einwilligung.'
            : 'Den ersten Seitenaufruf erfassen wir ohne Analyse-Cookies. Weitere Seitenaufrufe, Analyse-Cookies und detaillierte Webanalyse aktivieren wir erst nach Ihrer Einwilligung.'
          : 'Wir setzen keine optionalen Analyse- oder Performance-Cookies ein.'
        }
      </Typography>
    </DialogContent>
  </Dialog>
}

export default CookieBanner
